import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import {
  getWidgets,
  getWidget,
  createWidget,
  updateWidget,
  deleteWidget,
} from "@/lib/api/widgets";
import type { CreateWidgetRequest, UpdateWidgetRequest } from "@/types/widget";

interface UseWidgetsQueryOptions {
  refetchInterval?: number | false;
}

export const useWidgetsQuery = (options?: UseWidgetsQueryOptions) =>
  useQuery({
    queryKey: ["widgets"],
    queryFn: getWidgets,
    refetchInterval: options?.refetchInterval,
    refetchIntervalInBackground: false,
  });

export const useWidgetQuery = (id: string) =>
  useQuery({
    queryKey: ["widget", id],
    queryFn: () => getWidget(id).then((res) => res.data),
    enabled: !!id,
  });

export const useCreateWidgetMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateWidgetRequest) =>
      createWidget(data).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["widgets"] });
    },
  });
};

export const useUpdateWidgetMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateWidgetRequest }) =>
      updateWidget(id, data).then((res) => res.data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["widgets"] });
      queryClient.invalidateQueries({ queryKey: ["widget", variables.id] });
    },
  });
};

export const useDeleteWidgetMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteWidget(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["widgets"] });
    },
  });
};
